import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SetupOverviewItem, SetupType } from '../../../services/setupOverview/setupOverviewService';
import { cn } from '../../../lib/utils';
import { borderPrimary, cardClass, primaryText, secondaryText, whiteBG } from '../../../styling/stylingHelper';
import { CategoryBadge } from '../../../utils/categoryHelper';
import { TableComponent, type TableComponentProps } from '../../ui/TableComponent';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { filterSetupItems, CategorySetupFilter, CostSetupFilter } from './setupOverviewItems';
import { useCostPriceDishData } from '../../../hooks/useCostPriceDishData';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { getCostTargetForDishType, isCostBad, resolveCostTargets } from '../../../utils/costTargets';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { DropdownSelector } from '../../ui/DropdownSelector';

const NO_PLACEHOLDERS: string[] = [];

export function SetupOverviewItemsTable({ items, restaurantId, type, busy, onEditCategory, onEditCost }: {
  items: SetupOverviewItem[]; restaurantId?: string; type: SetupType; busy: boolean;
  onEditCategory: (item: SetupOverviewItem) => void;
  onEditCost: (item: SetupOverviewItem) => void;
}) {
  const { t } = useTranslation();
  const settings = useRestaurantStore(state => restaurantId ? state.reportSettings[restaurantId] : undefined);
  const costTargets = useMemo(() => resolveCostTargets(settings?.costTargets), [settings?.costTargets]);
  const { dishData } = useCostPriceDishData({ restaurantId, revenueItems: NO_PLACEHOLDERS, costTargets,
    defaultWastePercentage: resolveWastePercentage(settings?.wastePercentage) });
  const dishesById = useMemo(() => new Map(dishData.map(dish => [dish.id, dish])), [dishData]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategorySetupFilter>('all');
  const [cost, setCost] = useState<CostSetupFilter>('all');
  const [visibleCount, setVisibleCount] = useState(20);
  const rows = useMemo(() => filterSetupItems(items, { restaurantId, type, search, category, cost }),
    [items, restaurantId, type, search, category, cost]);
  const categoryOptions = [
    { value: 'all', label: t('overallType.all') },
    { value: 'missing', label: t('common.unassigned') },
    { value: 'complete', label: t('setupOverview.categorized') },
  ];
  const costOptions = [
    { value: 'all', label: t('overallType.all') },
    { value: 'missing', label: t('setupOverview.notSetUp') },
    { value: 'noIngredients', label: t('setupOverview.noIngredients') },
    { value: 'complete', label: t('setupOverview.setUp') },
  ];
  const reset = () => { setSearch(''); setCategory('all'); setCost('all'); setVisibleCount(20); };
  const categoryButton = (item: SetupOverviewItem) => <button type="button" disabled={busy}
    aria-label={t('setupOverview.editCategory', { name: item.itemName })}
    className="min-h-[44px] disabled:opacity-50"
    onClick={event => { event.stopPropagation(); onEditCategory(item); }}>
    <CategoryBadge categoryName={item.categorized ? item.categoryName ?? t('common.unassigned') : 'Uncategorized'}
      categoryColor={item.categoryColor ?? undefined} isDefaultCategory={!item.categorized} />
  </button>;
  const nameButton = (item: SetupOverviewItem) => <button type="button" disabled={busy}
    className={cn('min-h-[44px] text-left font-medium disabled:opacity-50', primaryText)}
    onClick={event => { event.stopPropagation(); onEditCost(item); }}>{item.itemName}</button>;
  const status = (item: SetupOverviewItem) => {
    if (item.type === 'other') return t('setupOverview.notApplicable');
    if (!item.costSetUp) return t('setupOverview.notSetUp');
    const dish = item.costPriceId ? dishesById.get(item.costPriceId) : undefined;
    if (!dish || !dish.price || dish.isPlaceholder || dish.totalCost === 0) return <span className={secondaryText}>—</span>;
    const bad = isCostBad(dish.costPercentage, getCostTargetForDishType(dish.revenueSourceType, costTargets));
    return <span className={cn('inline-flex items-center rounded-md p-2 text-[14px] leading-[10px] font-semibold',
      bad ? 'bg-[#FFF4E6] dark:bg-[#FFF4E6]/20 text-[#eb8d00] dark:text-[#eb8d00]'
        : 'bg-[#E7FBF5] dark:bg-[#E7FBF5]/20 text-[#069A6E] dark:text-[#16B364]')}>
      {dish.costPercentage.toFixed(1)}%
    </span>;
  };
  const columns: TableComponentProps<SetupOverviewItem>['columns'] = [
    { key: 'itemName', label: t('setupOverview.itemName'), render: (_, row) => nameButton(row) },
    ...(!restaurantId ? [{ key: 'restaurantName' as const, label: t('setupOverview.restaurant') }] : []),
    { key: 'type', label: t('setupOverview.itemType'), render: (_, row) => <span className={cn('rounded-md px-2 py-1 text-sm', whiteBG, secondaryText)}>{t(`overallType.${row.type}`)}</span> },
    { key: 'categoryName', label: t('setupOverview.category'), render: (_, row) => categoryButton(row),
      filter: { choice: { label: t('setupOverview.category'), value: category,
        onChange: value => { setCategory(value as CategorySetupFilter); setVisibleCount(20); }, options: categoryOptions } } },
    { key: 'ingredientCount', label: t('setupOverview.ingredients'), render: value => String(value) },
    { key: 'costSetUp', label: t('setupOverview.cost'), render: (_, row) => status(row),
      filter: { choice: { presentation: 'dropdown', label: t('setupOverview.cost'), value: cost,
        onChange: value => { setCost(value as CostSetupFilter); setVisibleCount(20); }, options: costOptions } } },
  ];
  const inputClass = cn('w-full rounded-xl border p-3', borderPrimary, whiteBG, primaryText);
  return <section className="mt-6" aria-label={t('setupOverview.items')}>
    <ResponsiveTable desktop={<TableComponent title={t('setupOverview.items')} data={rows} columns={columns}
      blankLines={false} usePagination onRowClick={onEditCost} isRowClickable={() => !busy}
      searchQuery={search} onSearchChange={value => { setSearch(value); setVisibleCount(20); }}
      searchPlaceholder={t('setupOverview.searchItems')} onClearFilters={reset}
      activeFilterCount={Number(category !== 'all') + Number(cost !== 'all') + Number(Boolean(search))} />}
      mobile={<div className="space-y-3">
        <h2 className={cn('text-lg font-semibold', primaryText)}>{t('setupOverview.items')}</h2>
        <input className={inputClass} value={search} aria-label={t('setupOverview.searchItems')}
          placeholder={t('setupOverview.searchItems')} onChange={event => { setSearch(event.target.value); setVisibleCount(20); }} />
        <label className={cn('block text-sm', secondaryText)}>{t('setupOverview.category')}
          <select className={inputClass} value={category} onChange={event => { setCategory(event.target.value as CategorySetupFilter); setVisibleCount(20); }}>
            {categoryOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label className={cn('block text-sm', secondaryText)}>{t('setupOverview.cost')}
          <DropdownSelector options={costOptions} defaultValue={cost} alwaysFullWidth
            onChange={value => { setCost(value as CostSetupFilter); setVisibleCount(20); }} />
        </label>
        <MobileCardList items={rows.slice(0, visibleCount)} keyOf={item => JSON.stringify([item.restaurantId, item.itemName])}
          hasMore={rows.length > visibleCount} onLoadMore={() => setVisibleCount(value => value + 20)} loadMoreLabel={t('setupOverview.loadMore')}
          renderCard={item => <div className={cn(cardClass, 'p-4 space-y-2')}>
            {nameButton(item)}
            {!restaurantId && <p className={secondaryText}>{item.restaurantName}</p>}
            <MobileCardField label={t('setupOverview.itemType')} value={t(`overallType.${item.type}`)} />
            <MobileCardField label={t('setupOverview.category')} value={categoryButton(item)} />
            <MobileCardField label={t('setupOverview.ingredients')} value={item.ingredientCount} />
            <MobileCardField label={t('setupOverview.cost')} value={status(item)} />
          </div>} />
        {!rows.length && <p className={secondaryText}>{t('setupOverview.noMatchingItems')}</p>}
      </div>} />
  </section>;
}
